import { motion } from "framer-motion";
import { Phone, Mail, ArrowUpRight } from "lucide-react";
import { BUSINESS, BOOKING_URL } from "@/config";


const HERO_TEXTURE =
  "https://static.prod-images.emergentagent.com/jobs/253c89d7-3b8a-419a-a470-22784a1036f8/images/5b370648bfb3db2c5ae63bb999d924c0b6c6e13e88b2fbcdacacbe1d4f5df299.png";

const Hero = () => {
  return (
    <section
      id="hero"
      className="relative min-h-[92vh] flex items-center overflow-hidden grain-overlay"
      data-testid="hero-section"
    >
      {/* Background layers */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_TEXTURE})` }}
        aria-hidden
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(135deg, rgba(11,19,43,0.94) 0%, rgba(21,29,51,0.88) 45%, rgba(44,55,77,0.78) 100%)",
        }}
        aria-hidden
      />
      <div className="absolute inset-0 hatch-pattern opacity-60" aria-hidden />

      {/* Watermark logo (right side, very subtle) */}
      <div
        className="absolute inset-y-0 right-0 w-full md:w-2/3 lg:w-1/2 pointer-events-none flex items-center justify-end pr-0 md:pr-8 lg:pr-16"
        aria-hidden
      >
        <img
          src={BUSINESS.springbokUrl}
          alt=""
          className="h-[60vh] md:h-[75vh] lg:h-[85vh] w-auto object-contain opacity-[0.07] md:opacity-[0.10] select-none"
          draggable={false}
        />
      </div>

      {/* Copper accent bar */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-40 bg-[#D47A4A] hidden lg:block" aria-hidden />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20 w-full">
        <div className="max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="inline-flex items-center gap-2 border border-[#D47A4A]/40 text-[#D47A4A] px-3 py-1 text-xs uppercase tracking-[0.25em] mb-8"
          >
            <span className="w-1.5 h-1.5 bg-[#D47A4A] rounded-full animate-pulse" />
            Edinburgh Local Tradesperson · Est. 2011
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-white leading-[0.95] tracking-tight"
          >
            EDINBURGH'S TRUSTED
            <br />
            <span className="text-[#D47A4A]">PLUMBING &amp; DRAIN</span>
            <br />
            CLEANING EXPERTS
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-8 text-lg md:text-xl text-[#A0ABC0] max-w-2xl leading-relaxed"
          >
            Professional, reliable &amp; available for emergency call-outs.
            Honest pricing, qualified workmanship, no nonsense — across Edinburgh
            and the surrounding areas.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4"
          >
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-primary-cta"
              className="group inline-flex items-center justify-center gap-2 bg-[#D47A4A] hover:bg-[#B85D2A] text-[#0B132B] font-bold px-9 py-[18px] rounded-md transition-all uppercase tracking-wider text-base shadow-lg shadow-[#D47A4A]/25 hover:shadow-[#D47A4A]/50 hover:-translate-y-0.5"
            >
              <Mail className="w-5 h-5" />
              Enquire Now
              <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
            <a
              href={BUSINESS.phoneHref}
              data-testid="hero-secondary-cta"
              className="inline-flex items-center justify-center gap-2 border border-[#2C374D] text-[#A0ABC0] hover:text-white hover:border-[#D47A4A]/60 px-7 py-4 rounded-md transition-colors uppercase tracking-wider text-sm"
            >
              <Phone className="w-4 h-4" />
              Call Now: {BUSINESS.phone}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-16 grid grid-cols-3 gap-6 max-w-2xl"
          >
            {[
              ["24/7", "Emergency Service"],
              ["Est. 2011", "Edinburgh Trade"],
              ["£0", "Hidden Charges"],
            ].map(([num, label]) => (
              <div key={label} className="border-l border-[#2C374D] pl-4">
                <div className="font-display text-3xl md:text-4xl text-white">{num}</div>
                <div className="text-xs uppercase tracking-widest text-[#A0ABC0] mt-1">
                  {label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
